import { useState } from "react";
import { ProposalProduct, UpdateWorksheetProductInput } from "generated/graphql";
import useUpdateProposalWorksheetItem from "hooks/proposals/useUpdateProposalWorksheetItem";
import useSaveProposalWorksheetItemAsProduct from "hooks/proposals/useSaveProposalWorksheetItemAsProduct";
import useUpdateWorksheetProduct from "hooks/worksheetProducts/useUpdateWorksheetProduct";
import { WorksheetItemInput } from "DDD/action-objects/WorksheetItemUpdate";
import {
  capturePricingBaseline,
  formValuesToProposalWorksheetInput,
  formValuesToWorksheetProductInput,
  getProposalItemFormValues,
} from "./worksheetFormValues";

export default function useWorksheetItemActions(product: ProposalProduct) {
  const [updateProposalWorksheetItem, itemSave] = useUpdateProposalWorksheetItem();
  const [saveProposalWorksheetItemAsProduct, saveAsProduct] =
    useSaveProposalWorksheetItemAsProduct();
  const [updateWorksheetProduct, libraryUpdate] = useUpdateWorksheetProduct();
  const [saving, setSaving] = useState(false);
  const [saveProductOpen, setSaveProductOpen] = useState(false);
  const [productTypeId, setProductTypeId] = useState("");
  const [internalName, setInternalName] = useState("");

  const handleSave = async (values: WorksheetItemInput): Promise<boolean> => {
    setSaving(true);
    const result = await updateProposalWorksheetItem({
      variables: { input: formValuesToProposalWorksheetInput(values) },
    });
    setSaving(false);

    return itemSave.isSuccess(result.data);
  };

  const handleUpdateLibrary = async (values: WorksheetItemInput) => {
    const saved = await handleSave(values);
    if (!saved || !product.worksheetProductSourceId) return;

    const input = formValuesToWorksheetProductInput(values) as UpdateWorksheetProductInput;
    const result = await updateWorksheetProduct({
      variables: {
        input: { ...input, id: product.worksheetProductSourceId } as UpdateWorksheetProductInput,
        expectedRevision: product.worksheetProductSourceRevision,
      },
    });

    if (!libraryUpdate.isSuccess(result.data)) {
      return;
    }

    const revision = libraryUpdate.getData(result)?.revision;
    if (revision) {
      await updateProposalWorksheetItem({
        variables: {
          input: {
            ...formValuesToProposalWorksheetInput(values),
            worksheetProductSourceRevision: revision,
          },
        },
      });
    }
  };

  const handleInlineUpdate = async (
    patch: Partial<WorksheetItemInput>,
    options: { captureBaseline?: boolean } = {}
  ): Promise<boolean> => {
    const current = getProposalItemFormValues(product);
    const values = { ...current, ...patch };

    if (
      options.captureBaseline &&
      patch.pricingOverrideField &&
      current.pricingOverrideField !== patch.pricingOverrideField
    ) {
      values.pricingBaseline = capturePricingBaseline(current);
    }

    const result = await updateProposalWorksheetItem({
      variables: { input: formValuesToProposalWorksheetInput(values) },
    });

    return itemSave.isSuccess(result.data);
  };

  const handleSaveAsProduct = async (values: WorksheetItemInput) => {
    const saved = await handleSave(values);
    if (!saved) return;
    setProductTypeId("");
    setInternalName("");
    setSaveProductOpen(true);
  };

  const handleConfirmSaveProduct = async () => {
    if (!productTypeId) return;
    const result = await saveProposalWorksheetItemAsProduct({
      variables: {
        proposalProductId: product.id,
        productTypeId,
        internalName: internalName || undefined,
      },
    });

    if (saveAsProduct.isSuccess(result.data)) {
      setSaveProductOpen(false);
    }
  };

  return {
    saving,
    savingProduct: saveAsProduct.loading,
    saveProductOpen,
    setSaveProductOpen,
    productTypeId,
    setProductTypeId,
    internalName,
    setInternalName,
    handleSave,
    handleInlineUpdate,
    handleUpdateLibrary,
    handleSaveAsProduct,
    handleConfirmSaveProduct,
  } as const;
}
